import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { zar, TIP_CATEGORY_LABELS } from "../lib/format";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import EmployeeForm from "../components/EmployeeForm";

export default function Employees() {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const [employees, setEmployees] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showFormer, setShowFormer] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/employees").then((r) => setEmployees(r.data)).catch(() => toast.error("Failed to load employees"));
  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    try {
      await api.post("/employees", payload);
      toast.success("Employee created");
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(apiError(e, "Create failed"));
    }
  };

  const statusColor = { active: "bg-[#A9C97E]/10 text-[#A9C97E]", inactive: "bg-[#D99A29]/10 text-[#8a6414]", terminated: "bg-[#F87171]/10 text-[#F87171]" };
  const former = employees.filter((e) => e.status === "terminated");
  const shown = showFormer ? former : employees.filter((e) => e.status !== "terminated");

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Employees</h1>
          <p className="text-muted-foreground mt-1">{employees.length} on record</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${API_BASE}/employees/export/csv`} data-testid="export-employees-csv">
            <Button variant="outline">Export CSV</Button>
          </a>
          <a href={`${API_BASE}/employees/export/pdf`} data-testid="export-employees-pdf">
            <Button variant="outline">Export PDF</Button>
          </a>
          <Button data-testid="add-employee-btn" onClick={() => setShowAdd(true)} className="bg-primary hover:bg-[#8B5CF6] text-white">
            Add employee
          </Button>
        </div>
      </div>

      <div className="flex gap-2" data-testid="emp-filter">
        {[["active", `Active (${employees.length - former.length})`], ["former", `Former (${former.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setShowFormer(k === "former")} data-testid={`emp-filter-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${((k === "former") === showFormer) ? "bg-primary text-white" : "bg-accent text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="glass border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Position</th>
              <th className="px-4 py-3 font-medium">Status</th>
              {isOwner && <th className="px-4 py-3 font-medium">Salary</th>}
              <th className="px-4 py-3 font-medium">Tip category</th>
              <th className="px-4 py-3 font-medium" title="Clocked hours this week / overtime above the weekly cap">This week</th>
              <th className="px-4 py-3 font-medium" title="Overtime hours month-to-date">Overtime (month)</th>
              <th className="px-4 py-3 font-medium">Role</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((e) => (
              <tr key={e.id} data-testid={`employee-row-${e.id}`} onClick={() => navigate(`/employees/${e.id}`)}
                className="border-b last:border-0 hover:bg-accent/60 cursor-pointer transition-colors">
                <td className="px-4 py-3 font-medium">{e.first_name} {e.last_name}</td>
                <td className="px-4 py-3">{e.position}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColor[e.status] || ""}`}>{e.status}</span></td>
                {isOwner && <td className="px-4 py-3 num">{e.salary_type === "hourly" ? `${zar(e.rate)}/h` : `${zar(e.rate)}/mo`}</td>}
                <td className="px-4 py-3">{TIP_CATEGORY_LABELS[e.tip_category] || "—"}</td>
                <td className="px-4 py-3 num" data-testid={`employee-week-hours-${e.id}`}>
                  {e.weekly_hours ?? 0}h
                  {(e.overtime_hours ?? 0) > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#F87171]/10 text-[#F87171]" data-testid={`employee-week-ot-${e.id}`}>
                      +{e.overtime_hours}h OT
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 num" data-testid={`employee-mtd-ot-${e.id}`}>
                  {(e.overtime_hours_mtd ?? 0) > 0 ? `${e.overtime_hours_mtd}h` : "—"}
                </td>
                <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{e.role}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-employee-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add employee</DialogTitle></DialogHeader>
          <EmployeeForm isNew onSubmit={create} submitLabel="Create employee" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
